import { ReactNode } from 'react';

import { Chip, Tooltip, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { Icon } from '../Icon';
import styles from './StatusIndicator.module.scss';
import { IconedLabel } from '@/components/ui/IconedLabel';
import { uiIcons } from '@/constants/uiIcons';

export type StatusIndicatorVariant = 'onTrack' | 'atRisk';

interface StatusIndicatorProps {
  variant: StatusIndicatorVariant;
  tooltip?: ReactNode;
  iconOnly?: boolean;
  showCursorPointer?: boolean;
}

export const StatusIndicator = ({
  variant,
  tooltip,
  iconOnly = false,
  showCursorPointer = false,
}: StatusIndicatorProps) => {
  const { t } = useTranslation('ui');
  const statusConfig = {
    onTrack: {
      icon: uiIcons.healthStatus.onTrack,
      label: t('healthStatus.OnTrack'),
      color: 'info' as const,
    },
    atRisk: {
      icon: uiIcons.healthStatus.atRisk,
      label: t('healthStatus.AtRisk'),
      color: 'error' as const,
    },
  } as const;
  const config = statusConfig[variant];

  if (!config) {
    console.error(`Invalid StatusIndicator variant: "${variant}"`);
    return <Typography variant="labelMono">{variant}</Typography>;
  }

  const content = iconOnly ? (
    <Chip
      size="small"
      label={<Icon name={config.icon} color={config.color} size="micro" />}
      color={config.color}
      className={showCursorPointer ? styles.ShowCursorPointer : undefined}
    />
  ) : (
    <Chip
      size="small"
      label={
        <IconedLabel
          iconName={config.icon}
          iconSize="micro"
          iconColor={config.color}
          typographyProps={{ color: config.color, variant: 'labelMono' }}
        >
          {config.label}
        </IconedLabel>
      }
      color={config.color}
    />
  );

  const defaultTooltip = tooltip || config.label;

  return tooltip || iconOnly ? (
    <Tooltip
      title={defaultTooltip}
      enterDelay={200}
      leaveDelay={200}
      disableInteractive
    >
      {content}
    </Tooltip>
  ) : (
    content
  );
};
